import React, { useCallback, useContext, useMemo, useState } from "react";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { toast } from "react-toastify";

import { CoreContext } from "context/CoreContext";
import { DoLogout } from "services/authentication";
import { ReadIdentitySession, IDENTITY_AREA_ROUTES } from 'services/identity';
import { getPortalConfig, normalizePortal } from "utils/portal";
import { languages } from "utils/options";
import { parseStrapiImage } from "utils";
import { Icon } from "ui/styled";
import Input from "components/Form/Input";

import {
    HomeActionButton,
    HomeHeaderActions,
    HomeDesktopActions,
    HomeActionText,
    HomeDropdown,
    HomeDropdownItem,
    HomeDropdownWrap,
    HomeHeaderButton,
    HomeHeaderContainer,
    HomeHeaderDismissLayer,
    HomeHeaderSearch,
    HomeHeaderSide,
    HomeMenuGlyph,
    HomeNotificationBadge,
    HomeUserAvatar,
    HomeUserAvatarInitials,
    HomeUserIdentity,
} from "./styled";

import useI18n from "hooks/useI18n";
import useNotificationCenter from "hooks/useNotificationCenter";
import DashboardSide from "../Side";
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

export default function DashboardHeader({ portal }) {
    const history = useHistory();
    const resolvedPortal = normalizePortal(portal);
    const portalConfig = useMemo(() => getPortalConfig(resolvedPortal), [resolvedPortal]);
    const loginRoute = portalConfig.loginRoute;
    const myAccountRoute = portalConfig.myAccountRoute;
    const certificatesRoute = portalConfig.certificatesRoute;

    const { user, themeMode, toggleThemeMode, searchExpression, setSearchExpression } = useContext(CoreContext);
    const { language, setLanguage, t } = useI18n();
    const { unreadCount } = useNotificationCenter();
    const identity = ReadIdentitySession();

    const [drawerOpened, setDrawerOpened] = useState(false);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [languageMenuOpen, setLanguageMenuOpen] = useState(false);

    const currentLanguage = useMemo(
        () => languages.find((item) => item.id === language) || languages[0],
        [language]
    );
    const userFullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim().toUpperCase() || 'USER';
    const userAvatarUrl = user?.image?.url ? parseStrapiImage(user.image.url) : null;
    const userInitials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`.trim() || 'U';

    const closeMenus = () => {
        setUserMenuOpen(false);
        setLanguageMenuOpen(false);
    };

    const exit = useCallback(async () => {
        await DoLogout();
        history.push(loginRoute);
    }, [history, loginRoute]);

    const handleSearch = useCallback(() => {
        const searchTerm = `${searchExpression || ''}`.trim();

        if (searchTerm.length < 2) {
            toast.error('It is necessary to have at least 2 characters.');
            return;
        }

        history.push(`/search?q=${encodeURIComponent(searchTerm)}`);
    }, [history, searchExpression]);

    const userOptions = useMemo(() => [
        ...(identity?.centralUserId ? [{ title: t('identity_access_title'), action: () => history.push(
            resolvedPortal === 'essentials' ? '/mvu-lite/access' : resolvedPortal === 'accelerator' ? '/mvu-accelerator/access' : '/access'
        ) }] : []),
        ...(identity?.areas?.length > 1 ? identity.areas : []).map(area => ({
            title: area === 'universe' ? 'MagVenture Universe' : area === 'lite' ? 'MagVenture Universe Lite' : 'MagVenture Accelerator',
            action: () => history.push(IDENTITY_AREA_ROUTES[area]),
        })),
        ...(myAccountRoute ? [{ title: t("my_account"), action: () => history.push(myAccountRoute) }] : []),
        ...(certificatesRoute ? [{ title: t("my_certificates"), action: () => history.push(certificatesRoute) }] : []),
        { title: t("logout"), action: exit },
    ], [certificatesRoute, exit, history, myAccountRoute, t, user, resolvedPortal, identity]);

    return (
        <>
            {(userMenuOpen || languageMenuOpen) ? <HomeHeaderDismissLayer onClick={closeMenus} /> : null}

            <HomeHeaderContainer>
                <HomeHeaderSide>
                    <HomeHeaderButton aria-label="Open navigation menu" onClick={() => setDrawerOpened(true)}>
                        <HomeMenuGlyph />
                    </HomeHeaderButton>
                    <Icon icon={themeMode === 'light' ? 'MV_Universe_Logo' : 'MV_Universe_Logo_white'} width={'280px'} onClick={() => history.push(portalConfig.homeRoute)} pointer />
                </HomeHeaderSide>

                {portalConfig.searchEnabled ? (
                    <HomeHeaderSearch>
                        <Input
                            placeholder={t("search")}
                            type="search"
                            holder
                            value={searchExpression}
                            action={handleSearch}
                            onChange={(event) => setSearchExpression(event.target.value)}
                            onSubmitEditing={handleSearch}
                            small
                        />
                    </HomeHeaderSearch>
                ) : null}

                <HomeHeaderActions>
                    <HomeActionButton
                        aria-label={themeMode === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
                        iconOnly
                        onClick={toggleThemeMode}
                    >
                        <Icon icon={themeMode === 'light' ? 'theme-toggle-light' : 'theme-toggle-dark'} width="20px" height="20px" nomargin />
                    </HomeActionButton>

                    <HomeActionButton
                        aria-label={t('notifications')}
                        iconOnly
                        onClick={() => history.push(portalConfig.notificationsRoute)}
                    >
                        <NotificationsNoneIcon aria-hidden="true" fontSize="small" />
                        {unreadCount ? (
                            <HomeNotificationBadge aria-label={`${unreadCount} ${t('unread_notifications')}`}>
                                {unreadCount > 99 ? '99+' : unreadCount}
                            </HomeNotificationBadge>
                        ) : null}
                    </HomeActionButton>

                    <HomeDesktopActions>
                        <HomeDropdownWrap>
                            <HomeActionButton onClick={() => {
                                setLanguageMenuOpen((value) => !value);
                                setUserMenuOpen(false);
                            }}>
                                <Icon icon={themeMode === 'light' ? 'word-light' : 'word-dark'} />
                                <HomeActionText>{currentLanguage?.name || 'ENG - English'}</HomeActionText>
                                <Icon icon={themeMode === 'light' ? 'chevron-down' : 'chevron-down-white'} width="24px" height={'24px'} nomargin />
                            </HomeActionButton>

                            {languageMenuOpen ? (
                                <HomeDropdown>
                                    {languages.map((item) => (
                                        <HomeDropdownItem
                                            key={item.id}
                                            onClick={() => {
                                                setLanguage(item.id);
                                                setLanguageMenuOpen(false);
                                            }}
                                        >
                                            {item.title}
                                        </HomeDropdownItem>
                                    ))}
                                </HomeDropdown>
                            ) : null}
                        </HomeDropdownWrap>

                        <HomeDropdownWrap>
                            <HomeActionButton onClick={() => { setUserMenuOpen((value) => !value); setLanguageMenuOpen(false); }}>
                                <HomeUserIdentity>
                                    <HomeUserAvatar image={userAvatarUrl}>
                                        {userAvatarUrl ? null : <HomeUserAvatarInitials>{userInitials}</HomeUserAvatarInitials>}
                                    </HomeUserAvatar>
                                    <HomeActionText>{userFullName}</HomeActionText>
                                </HomeUserIdentity>
                                <Icon icon={themeMode === 'light' ? 'chevron-down' : 'chevron-down-white'} width="24px" height={'24px'} nomargin />
                            </HomeActionButton>

                            {userMenuOpen ? (
                                <HomeDropdown>
                                    {userOptions.map((item) => (
                                        <HomeDropdownItem
                                            key={item.title}
                                            onClick={() => {
                                                closeMenus();
                                                item.action();
                                            }}
                                        >
                                            {item.title}
                                        </HomeDropdownItem>
                                    ))}
                                </HomeDropdown>
                            ) : null}
                        </HomeDropdownWrap>
                    </HomeDesktopActions>
                </HomeHeaderActions>
            </HomeHeaderContainer>
            <DashboardSide opened={drawerOpened} setOpened={setDrawerOpened} portal={resolvedPortal} />
        </>
    );
}
